import type { PeriodType } from '@/lib/api/queries';
import { RANGES, type RangeKey } from '@/stores/chartPrefsStore';

import { DEFAULT_SCALE, isPeriodType, isScaleMode, type ScaleMode } from './studio-metric';
import type { StudioLocation } from './MetricStudioPage';

/**
 * =============================================================================
 *  Metric Studio 的 typed search params —— **四條路由共用這一份**。
 *
 *  §2.6 路由那一列：「`?symbol`、`?range`、`?indicators`、`?as_of` 是**產品狀態**，
 *  必須可用 URL 定址才能分享。」明細頁的四個狀態同理：
 *
 *      ?range=1Y         視窗長度
 *      ?tab=…            一條路由下的第幾張表（驗收 #5）
 *      ?period_type=Y    季／年（驗收 #7）
 *      ?scale=raw        副軸切回原始值
 *
 *  ⚠️ **`period_type` 只在「年度」時出現在 URL 上。**
 *  ROADMAP §6 驗收 #7 逐字：「切到『年度』時 URL 帶 `period_type=Y` 且序列換成年度列；
 *  **不帶這個參數時只出現季度列**」。端點的預設就是 `Q`，所以把 `period_type=Q`
 *  寫進 URL 只是噪音；而更重要的是：**「不帶」這件事必須是可觀察的**，
 *  否則驗收 #7 的後半句沒有東西可以驗。
 *
 *  ⚠️ `lib/router/search-serializer.ts` 的推論：值長得像 JSON 的 search param 會被
 *  `JSON.parse` 讀成別的型別。這裡四個值分別是 `1Y` / `income` / `Y` / `raw`，
 *  `JSON.parse` 對它們全部丟出例外，所以讀回來一定是字串。
 *  （**不要**在這裡加一個像 `?page=2` 的參數而不做型別檢查——它會讀回數字。）
 * ========================================================================== */

export interface StudioSearch {
  range?: RangeKey;
  tab?: string;
  period_type?: PeriodType;
  scale?: ScaleMode;
}

/** 明細頁的預設視窗。1 年是這個產品裡「最近」的單位（KPI 區間條也是 1Y）。 */
export const DEFAULT_RANGE: RangeKey = '1Y';

function asRange(value: unknown): RangeKey | undefined {
  return typeof value === 'string' && (RANGES as readonly string[]).includes(value)
    ? (value as RangeKey)
    : undefined;
}

/**
 * `validateSearch`。**不合法的值一律丟掉而不是報錯**——一個被截斷的分享連結
 * 應該退化成這一頁的預設視圖，不該變成一個錯誤頁（與 `__root.tsx` 對 `as_of` 同理）。
 */
export function validateStudioSearch(search: Record<string, unknown>): StudioSearch {
  const out: StudioSearch = {};
  const range = asRange(search['range']);
  if (range !== undefined) out.range = range;
  const tab = search['tab'];
  if (typeof tab === 'string' && tab.trim() !== '') out.tab = tab.trim();
  const periodType = search['period_type'];
  if (isPeriodType(periodType)) out.period_type = periodType;
  const scale = search['scale'];
  if (isScaleMode(scale)) out.scale = scale;
  return out;
}

/** URL → `MetricStudioPage` 要的那個物件。預設值在這裡補齊，只補一次。 */
export function toStudioLocation(
  base: Pick<StudioLocation, 'market' | 'symbol' | 'metricKey'>,
  search: StudioSearch,
): StudioLocation {
  return {
    ...base,
    range: search.range ?? DEFAULT_RANGE,
    tab: search.tab,
    periodType: search.period_type,
    scale: search.scale ?? DEFAULT_SCALE,
  };
}

/**
 * `MetricStudioPage` 的 patch → 下一組 search params。
 *
 * **等於預設值的欄位會從 URL 上消失。** 這不是潔癖：
 *  · `?range=1Y&scale=pct` 是雜訊，而雜訊會讓真正有意義的參數難以一眼看見；
 *  · `period_type` 更是**語意**問題（見檔首）——「不帶」就是「季度」。
 *
 * 其餘 search params（`as_of`、以及任何未來新增的）原封不動帶著走。
 */
export function applyStudioPatch(
  previous: Record<string, unknown>,
  patch: Partial<StudioLocation>,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...previous };

  if ('range' in patch) {
    if (patch.range === undefined || patch.range === DEFAULT_RANGE) delete next['range'];
    else next['range'] = patch.range;
  }
  if ('tab' in patch) {
    if (patch.tab === undefined || patch.tab === '') delete next['tab'];
    else next['tab'] = patch.tab;
  }
  if ('periodType' in patch) {
    // 'Q' 與 undefined 都等於「不帶」——端點的預設就是 Q。
    if (patch.periodType === undefined || patch.periodType === 'Q') delete next['period_type'];
    else next['period_type'] = patch.periodType;
  }
  if ('scale' in patch) {
    if (patch.scale === undefined || patch.scale === DEFAULT_SCALE) delete next['scale'];
    else next['scale'] = patch.scale;
  }
  return next;
}
